import { useItemClassificationOptions } from '@hooks/menu';
import { isValidItemCd } from 'ebm-rw';
import { Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { fetchItemClassifications } from '@/renderer/services/ebmItemClassificationService';
import { updateOutletMenuItem } from '@/renderer/services/menuService';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import { Button } from '@components/common';
import { DropdownSelect } from '@components/common/DropdownSelect';
import { ItemCodeBuilderModal } from './ItemCodeBuilderModal';

interface FiscalTaxCategoryOption {
  id: number;
  code: string;
  name: string;
}

// The subset of MenuItemMissingFiscalSetup/MenuItemConfiguredFiscalSetup
// this editor actually needs — lets both fiscal-setup pages share one row
// editor regardless of which view backs them.
export interface FiscalSetupRow {
  menu_item_id: string;
  menu_item_name: string;
  outlet_id: string;
  outlet_name: string;
  fiscal_tax_category_id: number | null;
  ebm_item_cd: string | null;
  ebm_item_cls_cd: string | null;
}

export function FiscalSetupRowEditor({
  item,
  categories,
  onSaved,
}: {
  item: FiscalSetupRow;
  categories: FiscalTaxCategoryOption[];
  onSaved: () => void;
}) {
  const [categoryId, setCategoryId] = useState(
    item.fiscal_tax_category_id != null
      ? String(item.fiscal_tax_category_id)
      : '',
  );
  const [itemCd, setItemCd] = useState(item.ebm_item_cd ?? '');
  const [itemClsCd, setItemClsCd] = useState(item.ebm_item_cls_cd ?? '');
  const [itemClsCdName, setItemClsCdName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);

  const classOptions = useItemClassificationOptions();

  // Resolve the display name for an already-set classification code, since
  // it may not be on the first page of classOptions.items.
  useEffect(() => {
    if (!item.ebm_item_cls_cd) return;
    (async () => {
      const res = await fetchItemClassifications({
        search: item.ebm_item_cls_cd as string,
        pageSize: 5,
      });
      const match = res.data?.data.find(
        (c) => c.item_cls_cd === item.ebm_item_cls_cd,
      );
      if (match) setItemClsCdName(match.item_cls_nm);
    })().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.ebm_item_cls_cd]);

  const itemCdValid = isValidItemCd(itemCd.trim());
  const itemClsCdValid = itemClsCd.trim().length > 0;
  const canSave = categoryId !== '' && itemCdValid && itemClsCdValid;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateOutletMenuItem({
        id: item.menu_item_id,
        fiscal_tax_category_id: Number(categoryId),
        ebm_item_cd: itemCd.trim(),
        ebm_item_cls_cd: itemClsCd.trim(),
      });
      if (!result.success) {
        apiError(result.error || 'Failed to save fiscal setup');
        return;
      }
      apiSuccess(`Saved fiscal setup for ${item.menu_item_name}`);
      onSaved();
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const classDropdownOptions = [
    // Keep the currently-selected value selectable even if it falls off
    // the loaded/searched page (e.g. right after a fresh save).
    ...(itemClsCd &&
    !classOptions.items.some((c) => c.item_cls_cd === itemClsCd)
      ? [
          {
            value: itemClsCd,
            label: itemClsCdName
              ? `${itemClsCd} — ${itemClsCdName}`
              : itemClsCd,
          },
        ]
      : []),
    ...classOptions.items.map((c) => ({
      value: c.item_cls_cd,
      label: `${c.item_cls_cd} — ${c.item_cls_nm}`,
    })),
  ];

  return (
    <tr className="border-t border-gray-100">
      <td className="px-5 py-4 font-medium text-gray-900">
        {item.menu_item_name}
      </td>
      <td className="px-5 py-4 text-gray-600">{item.outlet_name}</td>
      <td className="px-5 py-4">
        <DropdownSelect
          value={categoryId}
          onChange={setCategoryId}
          options={categories.map((c) => ({
            value: String(c.id),
            label: `${c.code} — ${c.name}`,
          }))}
          placeholder="Select tax category"
        />
      </td>
      <td className="px-5 py-4">
        <button
          type="button"
          onClick={() => setIsCodeModalOpen(true)}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-pos-primary"
        >
          {itemCd ? (
            <span className="font-mono">{itemCd}</span>
          ) : (
            <span className="text-gray-400">Set item code</span>
          )}
        </button>
        {itemCd.trim().length > 0 && !itemCdValid && (
          <p className="mt-1 text-xs text-red-600">
            Not a valid RRA item code format.
          </p>
        )}
        <ItemCodeBuilderModal
          isOpen={isCodeModalOpen}
          onClose={() => setIsCodeModalOpen(false)}
          onConfirm={(code) => {
            setItemCd(code);
            setIsCodeModalOpen(false);
          }}
          outletId={item.outlet_id}
          initialItemCd={itemCd || null}
          itemName={item.menu_item_name}
        />
      </td>
      <td className="px-5 py-4">
        <DropdownSelect
          value={itemClsCd}
          onChange={(value) => {
            setItemClsCd(value);
            setItemClsCdName(
              classOptions.items.find((c) => c.item_cls_cd === value)
                ?.item_cls_nm ?? null,
            );
          }}
          options={classDropdownOptions}
          placeholder="Select classification code"
          searchable
          searchPlaceholder="Search by code or name…"
          onSearchChange={classOptions.setSearch}
          hasMoreOptions={classOptions.hasMore}
          isLoadingOptions={classOptions.isLoading}
          onLoadMoreOptions={classOptions.loadMore}
          emptyStateLabel="No classification codes synced yet — use Sync Classification Codes above."
        />
      </td>
      <td className="px-5 py-4">
        <Button size="sm" onClick={handleSave} disabled={!canSave || isSaving}>
          <Save size={14} />
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </td>
    </tr>
  );
}
